import { Link } from "react-router-dom";
import billPartsImage from "@/assets/editorial/article-illustrations/bill-basics/detail-1.webp";
import billScreenImage from "@/assets/editorial/article-illustrations/bill-basics/detail-2.webp";
import billSplitImage from "@/assets/editorial/article-illustrations/bill-basics/detail-3.webp";

function EditorialFigure({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="not-prose my-8 border-y border-background-300 bg-white py-5">
      <img src={src} alt={alt} className="mx-auto w-full max-w-[560px] object-contain" />
      <figcaption className="mt-4 px-3 text-center text-sm leading-6 text-foreground-600">{caption}</figcaption>
    </figure>
  );
}

const billParts = [
  {
    title: "基本料金・データ料金",
    description: "料金プラン、データ定額など。新しいプランと比べる中心の金額です。",
    point: "プラン比較に使う",
  },
  {
    title: "端末代",
    description: "分割支払金、機種代金など。乗り換えても支払いが残ることがあります。",
    point: "残額を別に確認",
  },
  {
    title: "通話料",
    description: "国内通話料、SMS、通話定額など。電話の使い方と合っているかを見ます。",
    point: "使い方と照らす",
  },
  {
    title: "オプション・その他",
    description: "端末補償、留守番電話、セキュリティ、キャリア決済などです。",
    point: "必要性を確認",
  },
];

const steps = [
  {
    title: "公式アプリか会員ページを開く",
    description: "検索広告からではなく、契約中の通信会社の公式アプリ・公式サイトからログインします。",
  },
  {
    title: "「料金」「請求額」「利用料金」を選ぶ",
    description: "トップ画面の合計額だけで終わらず、「内訳を見る」「詳細を確認」まで進みます。",
  },
  {
    title: "確認する月と電話番号を選ぶ",
    description: "家族分や自宅インターネットがまとまっている場合は、自分の回線を選びます。",
  },
  {
    title: "金額を4項目に分けてメモする",
    description: "基本料金・端末代・通話料・オプションに分けます。分からない項目は「その他」で構いません。",
  },
  {
    title: "直近3か月を並べる",
    description: "毎月続く料金と、その月だけ発生した料金を区別します。1か月だけでは判断しないのがポイントです。",
  },
];

const officialLinks = [
  {
    name: "ドコモ",
    description: "My docomoの「ご利用料金」から詳細・料金内訳へ",
    href: "https://www.docomo.ne.jp/support/online_confirmation/",
  },
  {
    name: "au・UQ mobile",
    description: "My auで請求額合計から、より詳細な内訳へ",
    href: "https://www.au.com/support/faq/details/00/0000/000003/pg00000350/",
  },
  {
    name: "SoftBank",
    description: "My SoftBankの請求金額から「明細を見る」へ",
    href: "https://www.softbank.jp/mobile/support/contract/charge-guide/procedure/online-charge/",
  },
  {
    name: "Y!mobile",
    description: "My Y!mobileの料金案内から「詳しい内訳を見る」へ",
    href: "https://www.ymobile.jp/support/online/billing/",
  },
  {
    name: "楽天モバイル",
    description: "my 楽天モバイルの利用料金から「利用明細を見る」へ",
    href: "https://network.mobile.rakuten.co.jp/guide/payment/usage-details/",
  },
];

export default function BillBasicsArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">
          「毎月8,000円も払っているから、3,000円のプランに変えれば5,000円安くなる」。そう思って乗り換え先を探し始めると、節約額を大きく見積もってしまうことがあります。
        </p>
        <p>
          スマホの請求には、通信プランだけでなく、端末の分割代金や通話料、有料オプションが一緒に入っているからです。まずは請求を4つに分けると、どこを見直せばよいかが見えてきます。
        </p>

        <div className="not-prose my-7 border-y border-[#D8CBAF] bg-[#FFFCF5] px-1 py-5 md:px-5">
          <p className="m-0 text-[16px] font-bold leading-7 text-foreground-900">
            この記事を読みながら、明細で次の3つを確かめます。
          </p>
          <ul className="mt-3 mb-0 grid list-none gap-x-8 gap-y-2 p-0 text-[15px] leading-7 text-foreground-700 md:grid-cols-3">
            <li>□ 通信費はいくらか</li>
            <li>□ 端末代は残っているか</li>
            <li>□ 使っていない料金はないか</li>
          </ul>
        </div>
      </section>

      <section id="answer">
        <h2>先に結論：合計額だけで比べない</h2>
        <p>
          新しいプランと比べるのは、原則として今の<strong>基本料金・データ料金と、必要な通話料</strong>です。端末代は、乗り換え後も支払いが続く可能性があるため、通信費とは別に考えます。
        </p>
        <div className="not-prose my-7 overflow-hidden rounded-xl border border-background-300 bg-white">
          <div className="grid items-center gap-2 px-2 py-5 md:grid-cols-[145px_1fr] md:px-4">
            <span className="text-sm font-bold text-foreground-500">毎月の請求</span>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <strong className="text-3xl text-foreground-900">8,000円</strong>
              <span className="text-sm text-foreground-600">＝ 通信費4,500円 ＋ 端末代3,000円 ＋ オプション500円</span>
            </div>
          </div>
          <div className="border-t border-background-300 bg-primary-50 px-2 py-4 md:px-4">
            <p className="m-0 text-[15px] leading-7 text-foreground-800">
              <strong className="mr-2 text-primary-700">比較するのは4,500円。</strong>
              端末代を含む8,000円と新しいプランの月額を比べると、節約額を大きく見積もってしまいます。
            </p>
          </div>
        </div>
        <p className="text-sm text-foreground-600">※金額は考え方を説明するための例です。実際の請求項目は契約内容によって異なります。</p>
      </section>

      <section id="bill-parts">
        <h2>スマホ代は、まず4つに分ける</h2>
        <p>明細の名称が少し違っていても、次の4つに分ければ十分です。最初から1円単位で合わせようとせず、大きな項目を見つけてください。</p>
        <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white">
          {billParts.map((part, index) => (
            <div key={part.title} className={`grid gap-x-4 gap-y-2 px-2 py-4 md:grid-cols-[210px_1fr_140px] md:items-center md:gap-6 md:px-4 md:py-5 ${index > 0 ? "border-t border-background-300" : ""}`}>
              <div className="flex items-baseline justify-between gap-4 md:block">
                <div className="text-[17px] font-bold text-foreground-900">{part.title}</div>
                <span className="shrink-0 text-xs font-bold text-primary-700 md:hidden">{part.point}</span>
              </div>
              <div className="text-sm leading-7 text-foreground-700">{part.description}</div>
              <span className="hidden text-sm font-bold text-primary-700 md:block md:text-right">{part.point}</span>
            </div>
          ))}
        </div>
        <aside>
          <strong>「その他」が残っても大丈夫です。</strong>
          <p>税金、ユニバーサルサービス料、キャリア決済、家族回線、自宅インターネットなどが含まれる場合があります。分からない金額を無理に通信費へ足さず、いったん「その他」として残します。</p>
        </aside>
        <EditorialFigure src={billPartsImage} alt="スマホの請求を4項目に分けて確認するイラスト" caption="請求総額を、通信費・端末代・通話料・オプションに分けて見ます。" />
      </section>

      <section id="screen-steps">
        <h2>請求明細を確認する5ステップ</h2>
        <p>用意するものは、契約中のスマホと直近3か月の明細、メモだけです。紙の請求書がなくても、公式アプリや会員ページから確認できます。</p>
        <ol className="not-prose relative my-7 list-none border-y-2 border-foreground-900 p-0">
          {steps.map((step, index) => (
            <li key={step.title} className={`grid grid-cols-[36px_1fr] gap-4 px-1 py-5 md:grid-cols-[48px_1fr] md:px-4 ${index > 0 ? "border-t border-background-300" : ""}`}>
              <span className="pt-0.5 text-sm font-bold text-primary-600">STEP {index + 1}</span>
              <div>
                <div className="text-[16px] font-bold leading-7 text-foreground-900">{step.title}</div>
                <div className="mt-1 text-sm leading-7 text-foreground-700">{step.description}</div>
              </div>
            </li>
          ))}
        </ol>
        <EditorialFigure src={billScreenImage} alt="スマートフォンの料金明細画面を確認するイラスト" caption="合計額だけで終わらず、公式アプリや会員ページで明細の内訳まで開きます。" />
      </section>

      <section id="example">
        <h2>月8,000円の明細で見てみる</h2>
        <p>たとえば、明細に次の4項目があったとします。合計8,000円でも、見直す対象と、別に確認する金額は同じではありません。</p>
        <div className="not-prose my-7 overflow-hidden border-y-2 border-foreground-900 bg-white">
          <div className="flex items-end justify-between gap-4 bg-foreground-900 px-5 py-4 text-white md:px-6">
            <span className="text-sm font-bold">請求額の例</span>
            <strong className="text-2xl">合計 8,000円</strong>
          </div>
          <div className="divide-y divide-background-200 px-5 md:px-6">
            <div className="grid grid-cols-[1fr_auto] gap-4 py-4 md:grid-cols-[1fr_110px_190px]">
              <div><strong className="block text-foreground-900">基本料金・データ</strong><span className="text-xs text-foreground-500">料金プラン、データ定額</span></div>
              <strong className="text-foreground-900">4,200円</strong>
              <span className="col-span-2 text-sm font-bold text-primary-600 md:col-span-1">プラン比較に使う</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-4 py-4 md:grid-cols-[1fr_110px_190px]">
              <div><strong className="block text-foreground-900">端末分割支払金</strong><span className="text-xs text-foreground-500">スマホ本体の代金</span></div>
              <strong className="text-foreground-900">3,000円</strong>
              <span className="col-span-2 text-sm font-bold text-[#B35D74] md:col-span-1">残りの回数を別に確認</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-4 py-4 md:grid-cols-[1fr_110px_190px]">
              <div><strong className="block text-foreground-900">通話料</strong><span className="text-xs text-foreground-500">従量通話</span></div>
              <strong className="text-foreground-900">300円</strong>
              <span className="col-span-2 text-sm font-bold text-primary-600 md:col-span-1">電話の使い方と比較</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-4 py-4 md:grid-cols-[1fr_110px_190px]">
              <div><strong className="block text-foreground-900">補償・留守番電話</strong><span className="text-xs text-foreground-500">有料オプション</span></div>
              <strong className="text-foreground-900">500円</strong>
              <span className="col-span-2 text-sm font-bold text-secondary-700 md:col-span-1">必要性を確認</span>
            </div>
          </div>
        </div>
        <p>
          この例なら、比較の出発点は基本料金・データ料金4,200円と通話料300円を合わせた4,500円です。新しいプランに無料通話が含まれるか、通話オプションが必要かも一緒に見ます。端末代3,000円は、乗り換えただけでは消えません。
        </p>
        <EditorialFigure src={billSplitImage} alt="通信費と端末代を分けて比較するイラスト" caption="新しいプランと比べる通信費と、支払いが残る端末代を混ぜないことが大切です。" />
      </section>

      <section id="review-order">
        <h2>金額が分かったら、この順番で判断する</h2>
        <div className="not-prose my-6 divide-y divide-background-300 border-y border-background-300">
          <div className="grid gap-2 py-5 md:grid-cols-[180px_1fr] md:gap-6">
            <strong className="text-foreground-900">1. オプション</strong>
            <p className="m-0 text-[15px] leading-7 text-foreground-700">使っていない補償やサービスがないか確認します。ただし、端末補償は外すと再加入できない場合があるため、故障時の負担も考えて判断します。</p>
          </div>
          <div className="grid gap-2 py-5 md:grid-cols-[180px_1fr] md:gap-6">
            <strong className="text-foreground-900">2. 通話料</strong>
            <p className="m-0 text-[15px] leading-7 text-foreground-700">毎月の通話料が通話定額より少ないなら、定額を外す方が安いことがあります。反対に長電話が多い方は、月額だけでなく通話条件まで比べます。</p>
          </div>
          <div className="grid gap-2 py-5 md:grid-cols-[180px_1fr] md:gap-6">
            <strong className="text-foreground-900">3. データ容量</strong>
            <p className="m-0 text-[15px] leading-7 text-foreground-700">契約容量ではなく、直近3か月に実際に使った量を確認します。毎月余っているなら、小さい容量を候補にできます。</p>
          </div>
          <div className="grid gap-2 py-5 md:grid-cols-[180px_1fr] md:gap-6">
            <strong className="text-foreground-900">4. 基本料金</strong>
            <p className="m-0 text-[15px] leading-7 text-foreground-700">必要なデータ量・通話・店舗サポートを残したうえで、同じ条件のプランを比較します。安さだけでサポート条件を落とさないことも大切です。</p>
          </div>
        </div>
        <aside>
          <strong>家族割・光回線・カード割引は、変更前に確認。</strong>
          <p>1回線だけ変えることで、家族の割引額や自宅インターネットの料金まで変わる場合があります。「自分のスマホ代だけ安くなったか」ではなく、世帯全体の支払額で確認してください。</p>
        </aside>
      </section>

      <section id="terms">
        <h2>明細の名前が違うときの早見表</h2>
        <div className="overflow-x-auto rounded-lg border border-background-300 bg-white">
          <table className="m-0 min-w-[660px] border-0">
            <thead>
              <tr><th>分ける項目</th><th>明細に出やすい名前</th><th>見るポイント</th></tr>
            </thead>
            <tbody>
              <tr><th>基本料金</th><td>基本使用料、料金プラン、データ定額</td><td>新しいプランと比べる中心の金額</td></tr>
              <tr><th>端末代</th><td>分割支払金、機種代金、割賦金</td><td>残額・残りの支払回数・返却条件</td></tr>
              <tr><th>通話料</th><td>音声通話料、国内通話料、通話オプション</td><td>従量料金か定額料金か</td></tr>
              <tr><th>オプション</th><td>補償、留守番電話、セキュリティ、コンテンツ</td><td>今も使っているか、外した場合の影響</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="official-links">
        <h2>契約中の通信会社で、確認画面を開く</h2>
        <p>画面名は変更されることがあります。下記は各社の公式案内です。ログイン後は、合計額だけでなく「内訳」「詳しい内訳」「利用明細」まで進んでください。</p>
        <div>
          {officialLinks.map((item) => (
            <a key={item.name} href={item.href} target="_blank" rel="noreferrer">
              <strong>{item.name}</strong>
              <span>{item.description}</span>
            </a>
          ))}
        </div>
        <p className="text-sm text-foreground-600">確認日：2026年9月18日。ログインにはID・パスワードや暗証番号が必要な場合があります。当サイトに契約情報を入力することはありません。</p>
      </section>

      <section id="faq">
        <h2>よくある疑問</h2>
        <details>
          <summary>4項目に分けても合計額が合いません</summary>
          <p>税金、各種手数料、キャリア決済、家族回線、自宅インターネットなどが含まれている可能性があります。分からない部分は「その他」にまとめ、通信費へ無理に足さないでください。</p>
        </details>
        <details>
          <summary>古いプランなら、すぐ変更した方がよいですか？</summary>
          <p>古いことだけで損とは決められません。現在の割引、家族への影響、必要なデータ量やサポートを確認してから、新しいプランと同じ条件で比べます。</p>
        </details>
        <details>
          <summary>端末代が分からなくても比較できますか？</summary>
          <p>候補探しはできます。まず使うデータ量と電話の使い方からプラン候補を絞り、節約額を出す段階で端末代を除いた通信費同士を比べてください。</p>
        </details>
      </section>

      <section id="next-actions">
        <h2>内訳が分かったら、次の一歩</h2>
        <p>ここまでで、通信費とそれ以外を分けられれば十分です。数字を整理するか、使い方に合う候補を探すところへ進みましょう。</p>
        <div>
          <Link to="/bill-check"><small>明細の数字を整理する</small><strong>支払い内訳ツールへ</strong><span>→</span></Link>
          <Link to="/diagnosis"><small>自分に合う候補を探す</small><strong>3つの質問で診断する</strong><span>→</span></Link>
        </div>
        <p><Link to="/articles/before-switching">乗り換える前に確認したい5つのことを読む →</Link></p>
      </section>
    </>
  );
}

